import React, { useContext, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { DiffProvider } from 'src/components/diff-set/diff-provider';
import { DiffMetadataProps } from 'src/components/diff-set/diff-set';
import { usePullRequestDiffUrls } from 'src/components/diff-set/urls';
import { getResourceTimings } from 'src/components/performance-metrics/resource-timings';
import { publishBasePullRequestFact } from 'src/redux/pull-request/actions';
import {
  getIsDifferentRepo,
  getPullRequestDestinationHash,
  getPullRequestSourceHash,
  getPullRequestSourceRepo,
} from 'src/redux/pull-request/selectors';
import { useParams } from 'src/router/hooks';

import { DiffStateContext, SelectedDiffType } from './diff-state-context';

type Props = {
  children: React.ReactNode;
};

export const PullRequestDiffProvider = (props: Props) => {
  const dispatch = useDispatch();
  const { repositoryOwner, repositorySlug, pullRequestId } = useParams<{
    repositoryOwner: string;
    repositorySlug: string;
    pullRequestId: string;
  }>();
  const prDestHash = useSelector(getPullRequestDestinationHash) || '';
  const prAnchorHash = useSelector(getPullRequestSourceHash) || '';
  const sourceRepoFullSlug =
    useSelector(getPullRequestSourceRepo)?.full_name || '';
  const destinationRepoFullSlug = `${repositoryOwner}/${repositorySlug}`;
  const { iterativeReviewSelectedDiff } = useContext(DiffStateContext);
  const sourceRepoDifferentDestRepo = useSelector(getIsDifferentRepo);
  const selectedHashIsDestHash = useMemo(
    () => iterativeReviewSelectedDiff.destHash === prDestHash,
    [iterativeReviewSelectedDiff.destHash, prDestHash]
  );

  let sourceHash = prAnchorHash;
  let destinationHash: string | undefined = prDestHash;
  // SelectedDiffType.None means we haven't loaded iterative review yet, e.g.
  // on the Overview tab. We can still use the default values to load initial
  // data
  if (iterativeReviewSelectedDiff.selectedDiffType !== SelectedDiffType.None) {
    const { sourceHash: source, destHash: dest } = iterativeReviewSelectedDiff;
    sourceHash = source;
    destinationHash =
      iterativeReviewSelectedDiff.selectedDiffType !== SelectedDiffType.Single
        ? dest
        : undefined;
  }

  const { diffUrlBuilder, diffStatUrlBuilder } = usePullRequestDiffUrls(
    true,
    pullRequestId,
    sourceRepoDifferentDestRepo && selectedHashIsDestHash
      ? sourceRepoFullSlug
      : '',
    sourceHash,
    destinationHash,
    destinationRepoFullSlug
  );

  const diffMetadataProps: DiffMetadataProps = useMemo(
    () => ({
      sourceHash,
      destinationHash,
      sourceRepoFullSlug,
      destinationRepoFullSlug: selectedHashIsDestHash
        ? destinationRepoFullSlug
        : sourceRepoFullSlug,
    }),
    [
      sourceHash,
      destinationHash,
      sourceRepoFullSlug,
      destinationRepoFullSlug,
      selectedHashIsDestHash,
    ]
  );

  const singleHashDiff =
    iterativeReviewSelectedDiff.selectedDiffType === SelectedDiffType.Single;

  const publishDiffsFetchedFact = () => {
    let timings = getResourceTimings('/diff?');
    if (!timings.present) {
      timings = getResourceTimings('/diff/');
    }
    const extraFactData = {
      encoded_body_size: timings.encodedBodySize,
      request_waiting_time: timings.requestWaitingTime,
      response_duration: timings.responseDuration,
      total_fetch_duration: Math.floor(timings.duration) || null,
      transfer_size: timings.transferSize,
    };
    dispatch(
      publishBasePullRequestFact(
        'bitbucket.pullrequests.diffs.fetched',
        extraFactData
      )
    );
  };

  const publishDiffsParsedFact = () => {
    dispatch(publishBasePullRequestFact('bitbucket.pullrequests.diffs.parsed'));
  };

  const diffFetchOptions = {
    collectDiffFetchedAnalytics: publishDiffsFetchedFact,
    collectDiffParsedAnalytics: publishDiffsParsedFact,
  };

  return (
    <DiffProvider
      diffUrlBuilder={diffUrlBuilder}
      diffStatUrlBuilder={diffStatUrlBuilder}
      diffMetadataProps={diffMetadataProps}
      singleHashDiff={singleHashDiff}
      maxDiffStatPages={1}
      diffFetchOptions={diffFetchOptions}
    >
      {props.children}
    </DiffProvider>
  );
};
